import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { format, parseISO, startOfWeek, endOfWeek } from 'date-fns';
import { todayLocal, localDateString } from '../lib/dateHelpers';
import { getStale, setCached } from '../lib/pageCache';
import { 
  Building2, 
  Receipt, 
  Clock, 
  FileText, 
  Archive,
  DollarSign,
  Users,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  Download,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Eye,
  CreditCard,
  Banknote,
  Building,
  Trash2,
  Pencil,
  RefreshCw,
  Timer,
  Briefcase,
  TrendingUp,
  MapPin,
  Home,
  Truck,
  Wrench,
  Settings,
  BookOpen,
  ShoppingBag,
  Coffee
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Reusable status pills (replaces the prior <Select> filter). One pill per
// status with a live count badge. Keeps the dashboard readable at a glance
// — no two-click drilldown needed to see how many Paid items exist.
const StatusPills = ({ value, onChange, counts, testIdPrefix }) => {
  const opts = [
    { id: 'pending',  label: 'Pending',  count: counts.pending  || 0, tone: 'amber' },
    { id: 'approved', label: 'Approved', count: counts.approved || 0, tone: 'emerald' },
    { id: 'paid',     label: 'Paid',     count: counts.paid     || 0, tone: 'slate' },
    { id: 'all',      label: 'All',      count: counts.all      || 0, tone: 'slate' },
  ];
  const tones = {
    amber:   { active: 'bg-amber-500 text-slate-900 border-amber-500',     badge_active: 'bg-amber-900/20 text-amber-900' },
    emerald: { active: 'bg-emerald-500 text-white border-emerald-500',     badge_active: 'bg-white/25 text-white' },
    slate:   { active: 'bg-slate-900 text-white border-slate-900',         badge_active: 'bg-white/20 text-white' },
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {opts.map(o => {
        const active = value === o.id;
        const tone = tones[o.tone];
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
              active
                ? tone.active
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`${testIdPrefix}-${o.id}`}
          >
            {o.label}
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
              active ? tone.badge_active : 'bg-slate-100 text-slate-600'
            }`}>
              {o.count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const OfficeDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isSuperUser = user?.role === 'super_user';
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Expense Reports State
  const [expenseReports, setExpenseReports] = useState([]);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [expenseFilter, setExpenseFilter] = useState('pending'); // pending, approved, paid, all
  const [expenseDateRange, setExpenseDateRange] = useState({ start: '', end: '' });
  
  // Timesheets State
  const [timesheets, setTimesheets] = useState([]);
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [timesheetFilter, setTimesheetFilter] = useState('pending');
  
  // Payment Dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    method: 'Check',
    reference: ''
  });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [approvingId, setApprovingId] = useState(null); // Track which item is being approved
  
  // Preview Dialog
  const [showPreview, setShowPreview] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  
  // Receipt Viewer Dialog
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  
  // Edit Expense Dialog (for approvers)
  const [showEditExpenseDialog, setShowEditExpenseDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Timesheet Preview/Payment Dialogs
  const [showTimesheetPreview, setShowTimesheetPreview] = useState(false);
  const [previewTimesheet, setPreviewTimesheet] = useState(null);
  const [showTimesheetPaymentDialog, setShowTimesheetPaymentDialog] = useState(false);
  const [timesheetPaymentDetails, setTimesheetPaymentDetails] = useState({
    method: 'Direct Deposit',
    reference: ''
  });

  // Export Reports State
  const [exportReportType, setExportReportType] = useState('timesheets');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Field Tracker Team Data
  // Use LOCAL date (Eastern Time for this company) so opening the page late evening
  // doesn't show tomorrow's date when UTC has already rolled over.
  const _localToday = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const [fieldTrackerData, setFieldTrackerData] = useState(null);
  const [fieldTrackerLoading, setFieldTrackerLoading] = useState(false);
  const [ftSelectedDate, setFtSelectedDate] = useState(_localToday);
  const [ftSelectedWeek, setFtSelectedWeek] = useState(_localToday);
  const [expandedMembers, setExpandedMembers] = useState({}); // Track which members have expanded details

  // Shop activity categories for display
  const SHOP_CATEGORIES = {
    'SHOP_PREP': { label: 'Shop Prep', icon: Settings, paid: true },
    'TRAINING': { label: 'Training', icon: BookOpen, paid: true },
    'ERRANDS': { label: 'Errands', icon: ShoppingBag, paid: true },
    'EQUIPMENT': { label: 'Equipment', icon: Wrench, paid: true },
    'END_OF_DAY_CLEANUP': { label: 'End of Day Cleanup', icon: Clock, paid: true },
    'LUNCH': { label: 'Lunch', icon: Coffee, paid: false },
    'OTHER': { label: 'Other', icon: FileText, paid: true }
  };

  // Toggle member details expansion
  const toggleMemberExpansion = (userId) => {
    setExpandedMembers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Summary stats
  const [stats, setStats] = useState({
    pendingTimesheets: 0,
    pendingTimesheetHours: 0,
    approvedTimesheets: 0,
    approvedTimesheetHours: 0,
    pendingExpenses: 0,
    pendingExpenseAmount: 0,
    approvedExpenses: 0,
    approvedExpenseAmount: 0,
  });

  useEffect(() => {
    // Stale-while-revalidate: paint from cache instantly if we have
    // it, then always refetch in the background so the numbers stay
    // fresh. Post-mortem 2026-08-13. Only runs on initial mount —
    // later mutation-triggered fetchData() calls skip this block.
    const cached = getStale('office-dashboard');
    if (cached) {
      setExpenseReports(cached.expenseReports || []);
      setTimesheets(cached.timesheets || []);
      if (cached.fieldTrackerData) setFieldTrackerData(cached.fieldTrackerData);
      setLoading(false);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      // Only show the full spinner if nothing is on screen yet (no
      // cache paint happened either). Mutation refetches skip the
      // spinner because content is already visible.
      if (expenseReports.length === 0 && timesheets.length === 0) setLoading(true);

      const tzOffset = new Date().getTimezoneOffset();
      // Parallelize the 3 fetches (post-mortem 2026-08-13). Was
      // sequential — each `await` blocked the next. `Promise.allSettled`
      // means a single failure (e.g. field-tracker permissions error)
      // doesn't abort the whole load.
      const [expResult, tsResult, ftResult] = await Promise.allSettled([
        axios.get(`${API}/api/expense-reports?all_users=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/timesheets/weekly?all_users=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/time-blocks/team/summary?date=${ftSelectedDate}&week_of=${ftSelectedWeek}&tz_offset=${tzOffset}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);

      const expenseReportsData = expResult.status === 'fulfilled' ? expResult.value.data : [];
      const timesheetsData = tsResult.status === 'fulfilled' ? tsResult.value.data : [];
      const fieldTrackerDataResp = ftResult.status === 'fulfilled' ? ftResult.value.data : null;

      setExpenseReports(expenseReportsData);
      setTimesheets(timesheetsData);
      if (fieldTrackerDataResp) setFieldTrackerData(fieldTrackerDataResp);

      // Cache the freshly-fetched data so a return visit within 60s
      // paints instantly.
      setCached('office-dashboard', {
        expenseReports: expenseReportsData,
        timesheets: timesheetsData,
        fieldTrackerData: fieldTrackerDataResp,
      });

      // Calculate stats for expenses
      const submittedExpenses = expenseReportsData.filter(r => r.status === 'Submitted');
      const approvedExpenses = expenseReportsData.filter(r => r.status === 'Approved');

      // Calculate stats for timesheets
      const submittedTimesheets = timesheetsData.filter(t => t.status === 'Submitted');
      const approvedTimesheets = timesheetsData.filter(t => t.status === 'Approved');
      // Float rollup of decimal hours produces noise like 66.7899999. Round
      // to 1 decimal for display so the dashboard reads cleanly.
      const pendingTimesheetHours = Math.round(
        submittedTimesheets.reduce((sum, t) => sum + (t.total_hours || 0), 0) * 10
      ) / 10;
      const approvedTimesheetHours = Math.round(
        approvedTimesheets.reduce((sum, t) => sum + (t.total_hours || 0), 0) * 10
      ) / 10;

      setStats({
        pendingExpenses: submittedExpenses.length,
        pendingExpenseAmount: submittedExpenses.reduce((sum, r) => sum + (r.total_amount || 0), 0),
        approvedExpenses: approvedExpenses.length,
        approvedExpenseAmount: approvedExpenses.reduce((sum, r) => sum + (r.total_amount || 0), 0),
        pendingTimesheets: submittedTimesheets.length,
        approvedTimesheets: approvedTimesheets.length,
        pendingTimesheetHours,
        approvedTimesheetHours,
      });
      
    } catch (error) {
      console.error('Error fetching office data:', error);
      toast.error('Failed to load office data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatMinutes = (mins) => {
    if (!mins && mins !== 0) return '0m';
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  // Fetch field tracker data when date/week changes
  const fetchFieldTrackerData = async () => {
    try {
      setFieldTrackerLoading(true);
      const tzOffset = new Date().getTimezoneOffset();
      const ftRes = await axios.get(`${API}/api/time-blocks/team/summary?date=${ftSelectedDate}&week_of=${ftSelectedWeek}&tz_offset=${tzOffset}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFieldTrackerData(ftRes.data);
    } catch (ftError) {
      console.log('Field tracker data not available:', ftError);
    } finally {
      setFieldTrackerLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchFieldTrackerData();
    }
  }, [ftSelectedDate, ftSelectedWeek, token]);

  // Day navigation for field tracker (all dates in local TZ — Eastern for this company)
  const goToPreviousDay = () => {
    const date = new Date(ftSelectedDate + 'T12:00:00');
    date.setDate(date.getDate() - 1);
    setFtSelectedDate(localDateString(date));
  };

  const goToNextDay = () => {
    const date = new Date(ftSelectedDate + 'T12:00:00');
    date.setDate(date.getDate() + 1);
    const today = todayLocal();
    if (localDateString(date) <= today) {
      setFtSelectedDate(localDateString(date));
    }
  };

  const goToToday = () => {
    setFtSelectedDate(todayLocal());
  };

  // Week navigation for field tracker
  const goToPreviousWeek = () => {
    const date = new Date(ftSelectedWeek + 'T12:00:00');
    date.setDate(date.getDate() - 7);
    setFtSelectedWeek(localDateString(date));
  };

  const goToNextWeek = () => {
    const date = new Date(ftSelectedWeek + 'T12:00:00');
    date.setDate(date.getDate() + 7);
    if (date <= new Date()) {
      setFtSelectedWeek(localDateString(date));
    }
  };

  const goToCurrentWeek = () => {
    setFtSelectedWeek(todayLocal());
  };

  const getStatusColor = (state) => {
    switch (state) {
      case 'SHOP': return 'bg-purple-100 text-purple-700';
      case 'TRAVEL': return 'bg-blue-100 text-blue-700';
      case 'WORK': 
      case 'ON_JOB': return 'bg-green-100 text-green-700';
      case 'IDLE': return 'bg-slate-100 text-slate-500';
      default: return 'bg-slate-100 text-slate-500';
    }
  };

  const getStatusLabel = (state) => {
    switch (state) {
      case 'SHOP': return 'At Shop';
      case 'TRAVEL': return 'Traveling';
      case 'WORK': 
      case 'ON_JOB': return 'On Job';
      case 'IDLE': return 'Not Tracking';
      default: return state;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Draft': 'bg-slate-100 text-slate-700',
      'Submitted': 'bg-blue-100 text-blue-700',
      'Approved': 'bg-green-100 text-green-700',
      'Rejected': 'bg-red-100 text-red-700',
      'Paid': 'bg-emerald-100 text-emerald-700 border border-emerald-300'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const getPaymentIcon = (method) => {
    switch (method) {
      case 'Check': return <FileText className="w-4 h-4" />;
      case 'Direct Deposit': return <Building className="w-4 h-4" />;
      case 'Cash': return <Banknote className="w-4 h-4" />;
      default: return <CreditCard className="w-4 h-4" />;
    }
  };

  const filteredExpenses = expenseReports.filter(report => {
    if (expenseFilter === 'pending') return report.status === 'Submitted';
    if (expenseFilter === 'approved') return report.status === 'Approved';
    if (expenseFilter === 'paid') return report.status === 'Paid';
    return true;
  });

  const handleApproveExpense = async (reportId) => {
    if (approvingId) return; // Prevent double-click
    setApprovingId(reportId);
    try {
      await axios.post(`${API}/api/expense-reports/${reportId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense report approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectExpense = async (reportId) => {
    const reason = prompt('Reason for rejection:');
    if (reason === null) return;
    
    try {
      await axios.post(`${API}/api/expense-reports/${reportId}/reject?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense report returned for corrections');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleMarkPaid = async () => {
    if (selectedExpenses.length === 0) {
      toast.error('Select at least one approved expense report');
      return;
    }
    
    // Verify all selected are approved
    const allApproved = selectedExpenses.every(id => {
      const report = expenseReports.find(r => r.id === id);
      return report?.status === 'Approved';
    });
    
    if (!allApproved) {
      toast.error('Only approved reports can be marked as paid');
      return;
    }
    
    setShowPaymentDialog(true);
  };

  const processPayment = async () => {
    setProcessingPayment(true);
    try {
      for (const reportId of selectedExpenses) {
        await axios.post(
          `${API}/api/expense-reports/${reportId}/mark-paid?payment_method=${encodeURIComponent(paymentDetails.method)}&payment_reference=${encodeURIComponent(paymentDetails.reference)}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success(`${selectedExpenses.length} expense report(s) marked as paid`);
      setShowPaymentDialog(false);
      setSelectedExpenses([]);
      setPaymentDetails({ method: 'Check', reference: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Handle approver editing an expense before approval
  const handleSaveExpenseEdit = async () => {
    if (!editingExpense) return;
    
    try {
      // Update the expense in the report
      const response = await axios.put(
        `${API}/api/expense-reports/${editingExpense.reportId}/expense/${editingExpense.index}`,
        {
          date: editingExpense.date,
          category: editingExpense.category,
          description: editingExpense.description,
          amount: editingExpense.amount
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Expense updated successfully');
      setShowEditExpenseDialog(false);
      setEditingExpense(null);
      
      // Refresh the preview report data
      if (previewReport) {
        const updatedReport = await axios.get(
          `${API}/api/expense-reports/${previewReport.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPreviewReport(updatedReport.data);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const approvedIds = filteredExpenses
        .filter(r => r.status === 'Approved')
        .map(r => r.id);
      setSelectedExpenses(approvedIds);
    } else {
      setSelectedExpenses([]);
    }
  };

  const handleSelectExpense = (reportId, checked) => {
    if (checked) {
      setSelectedExpenses(prev => [...prev, reportId]);
    } else {
      setSelectedExpenses(prev => prev.filter(id => id !== reportId));
    }
  };

  const handleExportCSV = () => {
    const data = filteredExpenses.map(r => ({
      'Report Number': r.report_number || '',
      'Employee': r.user_name,
      'Week': `Week ${r.week_number}, ${r.year}`,
      'Date Range': `${r.week_start} to ${r.week_end}`,
      'Total Amount': r.total_amount,
      'Status': r.status,
      'Submitted': r.submitted_at || '',
      'Approved By': r.approved_by || '',
      'Approved At': r.approved_at || '',
      'Payment Method': r.payment_method || '',
      'Payment Reference': r.payment_reference || '',
      'Paid At': r.paid_at || ''
    }));
    
    const headers = Object.keys(data[0] || {});
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expense_reports_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  // ==================== TIMESHEET HANDLERS ====================
  
  const filteredTimesheets = timesheets.filter(ts => {
    if (timesheetFilter === 'pending') return ts.status === 'Submitted';
    if (timesheetFilter === 'approved') return ts.status === 'Approved';
    if (timesheetFilter === 'paid') return ts.status === 'Paid';
    return true;
  });

  const handleApproveTimesheet = async (timesheetId) => {
    if (approvingId) return; // Prevent double-click
    setApprovingId(timesheetId);
    try {
      await axios.post(`${API}/api/timesheets/weekly/${timesheetId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Timesheet approved');
      fetchData();
      setShowTimesheetPreview(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve timesheet');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectTimesheet = async (timesheetId) => {
    const reason = prompt('Reason for rejection:');
    if (reason === null) return;
    
    try {
      await axios.post(`${API}/api/timesheets/weekly/${timesheetId}/reject?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Timesheet returned for corrections');
      fetchData();
      setShowTimesheetPreview(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject timesheet');
    }
  };

  const handleMarkTimesheetsPaid = async () => {
    if (selectedTimesheets.length === 0) {
      toast.error('Select at least one approved timesheet');
      return;
    }
    
    const allApproved = selectedTimesheets.every(id => {
      const ts = timesheets.find(t => t.id === id);
      return ts?.status === 'Approved';
    });
    
    if (!allApproved) {
      toast.error('Only approved timesheets can be marked as paid');
      return;
    }
    
    setShowTimesheetPaymentDialog(true);
  };

  const processTimesheetPayment = async () => {
    setProcessingPayment(true);
    try {
      for (const timesheetId of selectedTimesheets) {
        await axios.post(
          `${API}/api/timesheets/weekly/${timesheetId}/mark-paid?payment_method=${encodeURIComponent(timesheetPaymentDetails.method)}&payment_reference=${encodeURIComponent(timesheetPaymentDetails.reference)}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success(`${selectedTimesheets.length} timesheet(s) marked as paid`);
      setShowTimesheetPaymentDialog(false);
      setSelectedTimesheets([]);
      setTimesheetPaymentDetails({ method: 'Direct Deposit', reference: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleSelectAllTimesheets = (checked) => {
    if (checked) {
      const approvedIds = filteredTimesheets
        .filter(t => t.status === 'Approved')
        .map(t => t.id);
      setSelectedTimesheets(approvedIds);
    } else {
      setSelectedTimesheets([]);
    }
  };

  const handleSelectTimesheet = (timesheetId, checked) => {
    if (checked) {
      setSelectedTimesheets(prev => [...prev, timesheetId]);
    } else {
      setSelectedTimesheets(prev => prev.filter(id => id !== timesheetId));
    }
  };

  // Open a timesheet — unified UX: navigate to the focus-mode page
  // (/timesheet?user=&week=) instead of the old preview modal. Same data,
  // same look as the Approval Dashboard "click name" flow, plus inline
  // Approve/Reject/Reopen + edit/delete. Old modal is kept only as a
  // fallback (e.g. for `viewBookmark` hot-key) and is no longer wired up
  // to any UI button.
  const viewTimesheetDetails = (timesheet) => {
    if (!timesheet?.user_id || !timesheet?.week_start) {
      toast.error('Timesheet record is missing user/week — cannot open');
      return;
    }
    navigate(`/timesheet?user=${timesheet.user_id}&week=${timesheet.week_start}`);
  };

  const handleExportTimesheetsCSV = () => {
    const data = filteredTimesheets.map(t => ({
      'Timesheet Number': t.timesheet_number || '',
      'Employee': t.user_name,
      'Week': `Week ${t.week_number}, ${t.year}`,
      'Date Range': `${t.week_start} to ${t.week_end}`,
      'Total Hours': t.total_hours || 0,
      'Status': t.status,
      'Submitted': t.submitted_at || '',
      'Approved By': t.approved_by || '',
      'Approved At': t.approved_at || '',
      'Payment Method': t.payment_method || '',
      'Payment Reference': t.payment_reference || '',
      'Paid At': t.paid_at || ''
    }));
    
    const headers = Object.keys(data[0] || {});
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheets_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  // Generate Summary Report PDF
  const handleGenerateReportPDF = async () => {
    setGeneratingPDF(true);
    try {
      const params = new URLSearchParams({
        report_type: exportReportType
      });
      
      if (exportStartDate) params.append('start_date', exportStartDate);
      if (exportEndDate) params.append('end_date', exportEndDate);
      
      const response = await axios.get(`${API}/api/reports/summary/pdf?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${exportReportType}_summary_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report PDF generated');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-8 h-8 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] text-slate-900">Office Dashboard</h1>
            <p className="text-sm text-slate-500">Manage expenses, timesheets, and payroll</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
            Overview
          </TabsTrigger>
          <TabsTrigger value="timesheets" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
            Timesheets
          </TabsTrigger>
          <TabsTrigger value="expenses" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
            Expenses
          </TabsTrigger>
          <TabsTrigger value="reports" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
            Reports
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Summary Cards — Timesheets first, Expenses second */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-amber-200 bg-amber-50" data-testid="office-summary-card-ts-pending">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-700 font-semibold uppercase tracking-wide">Timesheets · Pending</p>
                    <p className="text-3xl font-bold text-amber-900 leading-none mt-1">{stats.pendingTimesheets}</p>
                    <p className="text-sm text-amber-700 mt-1">{stats.pendingTimesheetHours} hrs awaiting approval</p>
                  </div>
                  <Clock className="w-10 h-10 text-amber-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-emerald-200 bg-emerald-50" data-testid="office-summary-card-ts-approved">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-700 font-semibold uppercase tracking-wide">Timesheets · Approved</p>
                    <p className="text-3xl font-bold text-emerald-900 leading-none mt-1">{stats.approvedTimesheets}</p>
                    <p className="text-sm text-emerald-700 mt-1">{stats.approvedTimesheetHours} hrs ready to pay</p>
                  </div>
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50" data-testid="office-summary-card-exp-pending">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700 font-semibold uppercase tracking-wide">Expenses · Pending</p>
                    <p className="text-3xl font-bold text-blue-900 leading-none mt-1">{stats.pendingExpenses}</p>
                    <p className="text-sm text-blue-700 mt-1">{formatCurrency(stats.pendingExpenseAmount)} awaiting approval</p>
                  </div>
                  <AlertCircle className="w-10 h-10 text-blue-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50" data-testid="office-summary-card-exp-approved">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 font-semibold uppercase tracking-wide">Expenses · Approved</p>
                    <p className="text-3xl font-bold text-green-900 leading-none mt-1">{stats.approvedExpenses}</p>
                    <p className="text-sm text-green-700 mt-1">{formatCurrency(stats.approvedExpenseAmount)} ready to pay</p>
                  </div>
                  <DollarSign className="w-10 h-10 text-green-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed']">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button
                size="sm"
                onClick={() => { setActiveTab('timesheets'); setTimesheetFilter('pending'); }}
                className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                data-testid="quick-review-timesheets"
              >
                <Clock className="w-4 h-4 mr-2" />
                Approve Timesheets ({stats.pendingTimesheets})
              </Button>
              <Button
                size="sm"
                onClick={() => { setActiveTab('timesheets'); setTimesheetFilter('approved'); }}
                variant="outline"
                className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="quick-pay-timesheets"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Pay Timesheets ({stats.approvedTimesheets})
              </Button>
              <Button
                size="sm"
                onClick={() => { setActiveTab('expenses'); setExpenseFilter('pending'); }}
                variant="outline"
                className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="quick-review-expenses"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                Review Expenses ({stats.pendingExpenses})
              </Button>
              <Button
                size="sm"
                onClick={() => { setActiveTab('expenses'); setExpenseFilter('approved'); }}
                variant="outline"
                className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="quick-pay-expenses"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Pay Expenses ({stats.approvedExpenses})
              </Button>
              <Button
                size="sm"
                onClick={() => setActiveTab('reports')}
                variant="outline"
                className="h-9 border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="quick-generate-reports"
              >
                <FileText className="w-4 h-4 mr-2" />
                Generate Reports
              </Button>
            </CardContent>
          </Card>

          {/* Recent Submissions — timesheets + expenses, most recent first */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed']">Recent Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const tsItems = timesheets
                  .filter(t => t.status === 'Submitted')
                  .map(t => ({
                    id: `ts-${t.id}`,
                    type: 'TS',
                    user_name: t.user_name,
                    label: `Week ${t.week_number}, ${t.year}`,
                    right_top: `${(Math.round((t.total_hours || 0) * 10) / 10)} hrs`,
                    right_bottom: `${(t.entries?.length || 0)} entries`,
                    submitted_at: t.submitted_at || t.updated_at || t.created_at || '',
                    onClick: () => { setActiveTab('timesheets'); setTimesheetFilter('pending'); },
                  }));
                const expItems = expenseReports
                  .filter(r => r.status === 'Submitted')
                  .map(r => ({
                    id: `exp-${r.id}`,
                    type: 'EXP',
                    user_name: r.user_name,
                    label: `Week ${r.week_number}, ${r.year}`,
                    right_top: formatCurrency(r.total_amount),
                    right_bottom: `${r.expenses?.length || 0} expenses`,
                    submitted_at: r.submitted_at || r.updated_at || r.created_at || '',
                    onClick: () => { setActiveTab('expenses'); setExpenseFilter('pending'); },
                  }));
                const merged = [...tsItems, ...expItems]
                  .sort((a, b) => String(b.submitted_at).localeCompare(String(a.submitted_at)))
                  .slice(0, 6);
                if (merged.length === 0) {
                  return <p className="text-center text-slate-500 py-4">No pending submissions</p>;
                }
                return (
                  <div className="space-y-3" data-testid="recent-submissions">
                    {merged.map(item => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={item.onClick}
                        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-amber-50/60 rounded-lg text-left transition-colors"
                        data-testid={`recent-${item.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                            item.type === 'TS' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {item.type}
                          </span>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{item.user_name}</p>
                            <p className="text-xs text-slate-500">{item.label}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`font-bold ${item.type === 'TS' ? 'text-amber-700' : 'text-emerald-600'}`}>
                            {item.right_top}
                          </p>
                          <p className="text-xs text-slate-500">{item.right_bottom}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Team Field Activity Section */}
          {fieldTrackerData && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Timer className="w-6 h-6 text-amber-500" />
                Team Field Activity
                {fieldTrackerLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </h2>
              
              {/* Summary Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-cyan-200 bg-cyan-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-cyan-600 font-medium">Active Now</p>
                        <p className="text-2xl font-bold text-cyan-900">{fieldTrackerData.team_summary.active_now}</p>
                        <p className="text-sm text-cyan-600">of {fieldTrackerData.team_summary.total_members} members</p>
                      </div>
                      <Users className="w-10 h-10 text-cyan-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Day Total</p>
                        <p className="text-2xl font-bold text-purple-900">{formatMinutes(fieldTrackerData.team_summary.day_total_minutes)}</p>
                        <p className="text-sm text-purple-600">team hours</p>
                      </div>
                      <Clock className="w-10 h-10 text-purple-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-teal-200 bg-teal-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-teal-600 font-medium">Week Total</p>
                        <p className="text-2xl font-bold text-teal-900">{formatMinutes(fieldTrackerData.team_summary.week_total_minutes)}</p>
                        <p className="text-sm text-teal-600">total hours</p>
                      </div>
                      <TrendingUp className="w-10 h-10 text-teal-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-orange-600 font-medium">Not Tracking</p>
                        <p className="text-2xl font-bold text-orange-900">
                          {fieldTrackerData.team_summary.total_members - fieldTrackerData.team_summary.active_now}
                        </p>
                        <p className="text-sm text-orange-600">members idle</p>
                      </div>
                      <AlertCircle className="w-10 h-10 text-orange-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Status with Navigation */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-green-500" />
                      {fieldTrackerData.is_today ? 'Live Status - Today' : `Day View - ${fieldTrackerData.selected_date}`}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={goToPreviousDay}>
                        <ChevronDown className="w-4 h-4 rotate-90" />
                      </Button>
                      <Button 
                        variant={fieldTrackerData.is_today ? "default" : "outline"} 
                        size="sm" 
                        onClick={goToToday}
                        className={fieldTrackerData.is_today ? "bg-green-500 hover:bg-green-600" : ""}
                      >
                        Today
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={goToNextDay}
                        disabled={ftSelectedDate >= todayLocal()}
                      >
                        <ChevronDown className="w-4 h-4 -rotate-90" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {fieldTrackerData.daily_status.map(member => (
                      <div key={member.user_id} className="bg-slate-50 rounded-lg overflow-hidden">
                        {/* Member Header Row */}
                        <div 
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => toggleMemberExpansion(member.user_id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${member.current_status.state !== 'IDLE' && fieldTrackerData.is_today ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
                            <div>
                              <p className="font-medium">{member.full_name}</p>
                              <div className="flex items-center gap-2 text-sm">
                                {fieldTrackerData.is_today && (
                                  <>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(member.current_status.state)}`}>
                                      {getStatusLabel(member.current_status.state)}
                                    </span>
                                    {member.current_status.job_number && (
                                      <span className="text-slate-500">→ {member.current_status.job_number}</span>
                                    )}
                                    {member.current_status.active_minutes > 0 && (
                                      <span className="text-slate-400">({formatMinutes(member.current_status.active_minutes)})</span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right text-sm">
                              <div className="flex items-center gap-3">
                                <div className="text-purple-600" title="Shop">
                                  <span className="font-medium">{formatMinutes(member.day?.shop_minutes || 0)}</span>
                                </div>
                                <div className="text-blue-600" title="Travel">
                                  <span className="font-medium">{formatMinutes(member.day?.travel_minutes || 0)}</span>
                                </div>
                                <div className="text-green-600" title="Work">
                                  <span className="font-medium">{formatMinutes(member.day?.work_minutes || 0)}</span>
                                </div>
                                <div className="text-slate-900 font-bold border-l pl-3" title="Total">
                                  {formatMinutes(member.day?.total_minutes || 0)}
                                </div>
                              </div>
                              <p className="text-xs text-slate-400 mt-1">Shop | Travel | Work | Total</p>
                            </div>
                            <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedMembers[member.user_id] ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Expanded Breakdown */}
                        {expandedMembers[member.user_id] && (member.by_shop_category && Object.keys(member.by_shop_category).length > 0 || member.by_job && member.by_job.length > 0) && (
                          <div className="px-4 pb-4 pt-2 border-t border-slate-200 bg-white">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Shop Category Breakdown */}
                              {member.by_shop_category && Object.keys(member.by_shop_category).length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1">
                                    <Home className="w-3 h-3" /> Shop Breakdown
                                  </p>
                                  <div className="space-y-1">
                                    {Object.entries(member.by_shop_category).map(([categoryKey, data]) => {
                                      const realCategory = data.category || (categoryKey.startsWith('OTHER::') ? 'OTHER' : categoryKey);
                                      const catInfo = SHOP_CATEGORIES[realCategory] || { label: realCategory, icon: Settings, paid: true };
                                      const Icon = catInfo.icon;
                                      // For OTHER buckets, show the user's typed description instead of generic "Other"
                                      const displayLabel = (realCategory === 'OTHER' && data.description)
                                        ? data.description
                                        : catInfo.label;
                                      return (
                                        <div key={categoryKey} className="flex items-center justify-between text-sm bg-purple-50 px-2 py-1.5 rounded">
                                          <div className="flex items-center gap-2">
                                            <Icon className="w-3.5 h-3.5 text-purple-500" />
                                            <span className="text-slate-700">{displayLabel}</span>
                                            {!data.paid && <Badge variant="outline" className="text-xs py-0 h-4">unpaid</Badge>}
                                          </div>
                                          <span className={`font-medium ${data.paid ? 'text-purple-700' : 'text-slate-400'}`}>
                                            {formatMinutes(data.minutes)}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              
                              {/* Job Breakdown */}
                              {member.by_job && member.by_job.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                                    <Wrench className="w-3 h-3" /> Job Breakdown
                                  </p>
                                  <div className="space-y-1">
                                    {member.by_job.map((job) => (
                                      <div key={job.job_id} className="bg-slate-100 px-2 py-2 rounded">
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-sm text-slate-800">{job.job_number}</span>
                                          <span className="font-bold text-sm text-slate-900">
                                            {formatMinutes((job.travel_minutes || 0) + (job.work_minutes || 0))}
                                          </span>
                                        </div>
                                        {job.service_address && (
                                          <p className="text-xs text-slate-500 truncate">{job.service_address}</p>
                                        )}
                                        <div className="flex gap-3 mt-1 text-xs">
                                          {job.travel_minutes > 0 && (
                                            <span className="text-amber-600 flex items-center gap-1">
                                              <Truck className="w-3 h-3" /> {formatMinutes(job.travel_minutes)}
                                            </span>
                                          )}
                                          {job.work_minutes > 0 && (
                                            <span className="text-green-600 flex items-center gap-1">
                                              <Wrench className="w-3 h-3" /> {formatMinutes(job.work_minutes)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Show message if no breakdown data */}
                            {(!member.by_shop_category || Object.keys(member.by_shop_category).length === 0) && 
                             (!member.by_job || member.by_job.length === 0) && (
                              <p className="text-sm text-slate-400 text-center py-2">No detailed breakdown available</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {fieldTrackerData.daily_status.length === 0 && (
                      <p className="text-center text-slate-500 py-4">No team members found</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Totals with Navigation */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-amber-500" />
                      Weekly Totals
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">{fieldTrackerData.week_start} to {fieldTrackerData.week_end}</span>
                      <Button variant="outline" size="sm" onClick={goToPreviousWeek}>
                        <ChevronDown className="w-4 h-4 rotate-90" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={goToCurrentWeek}
                      >
                        This Week
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={goToNextWeek}
                        disabled={new Date(ftSelectedWeek) >= new Date()}
                      >
                        <ChevronDown className="w-4 h-4 -rotate-90" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium text-slate-600">Team Member</th>
                          <th className="text-right py-2 font-medium text-purple-600">Shop</th>
                          <th className="text-right py-2 font-medium text-blue-600">Travel</th>
                          <th className="text-right py-2 font-medium text-green-600">Work</th>
                          <th className="text-right py-2 font-medium text-slate-800">Total</th>
                          <th className="text-right py-2 font-medium text-slate-500">Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fieldTrackerData.weekly_totals.map(member => (
                          <tr key={member.user_id} className="border-b last:border-0 hover:bg-slate-50">
                            <td className="py-3 font-medium">{member.full_name}</td>
                            <td className="py-3 text-right text-purple-600">{formatMinutes(member.week.shop_minutes)}</td>
                            <td className="py-3 text-right text-blue-600">{formatMinutes(member.week.travel_minutes)}</td>
                            <td className="py-3 text-right text-green-600">{formatMinutes(member.week.work_minutes)}</td>
                            <td className="py-3 text-right font-bold">{formatMinutes(member.week.total_minutes)}</td>
                            <td className="py-3 text-right text-slate-500">{member.week.days_worked}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100 font-bold">
                          <td className="py-3">Team Total</td>
                          <td className="py-3 text-right text-purple-600">
                            {formatMinutes(fieldTrackerData.weekly_totals.reduce((sum, m) => sum + m.week.shop_minutes, 0))}
                          </td>
                          <td className="py-3 text-right text-blue-600">
                            {formatMinutes(fieldTrackerData.weekly_totals.reduce((sum, m) => sum + m.week.travel_minutes, 0))}
                          </td>
                          <td className="py-3 text-right text-green-600">
                            {formatMinutes(fieldTrackerData.weekly_totals.reduce((sum, m) => sum + m.week.work_minutes, 0))}
                          </td>
                          <td className="py-3 text-right">
                            {formatMinutes(fieldTrackerData.team_summary.week_total_minutes)}
                          </td>
                          <td className="py-3 text-right text-slate-500">-</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4 mt-6">
          {/* Filters and Actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <StatusPills
                  value={expenseFilter}
                  onChange={setExpenseFilter}
                  counts={{
                    pending:  expenseReports.filter(r => r.status === 'Submitted').length,
                    approved: expenseReports.filter(r => r.status === 'Approved').length,
                    paid:     expenseReports.filter(r => r.status === 'Paid').length,
                    all:      expenseReports.length,
                  }}
                  testIdPrefix="expense-pill"
                />
                <div className="flex flex-wrap gap-2">
                  {expenseFilter === 'approved' && selectedExpenses.length > 0 && (
                    <Button onClick={handleMarkPaid} className="bg-emerald-500 hover:bg-emerald-600">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Mark Paid ({selectedExpenses.length})
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleExportCSV}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expenses Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {expenseFilter === 'approved' && (
                        <TableHead className="w-10">
                          <Checkbox 
                            checked={selectedExpenses.length === filteredExpenses.filter(r => r.status === 'Approved').length && filteredExpenses.filter(r => r.status === 'Approved').length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                      )}
                      <TableHead>Employee</TableHead>
                      <TableHead>Week</TableHead>
                      <TableHead>Report #</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredExpenses.map(report => (
                      <TableRow key={report.id}>
                        {expenseFilter === 'approved' && (
                          <TableCell>
                            {report.status === 'Approved' && (
                              <Checkbox 
                                checked={selectedExpenses.includes(report.id)}
                                onCheckedChange={(checked) => handleSelectExpense(report.id, checked)}
                              />
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <div>
                            <p className="font-medium">{report.user_name}</p>
                            <p className="text-xs text-slate-500">{report.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">Week {report.week_number}, {report.year}</p>
                            <p className="text-xs text-slate-500">{report.week_start} to {report.week_end}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">{report.report_number || '-'}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-bold text-emerald-600">{formatCurrency(report.total_amount)}</span>
                          <p className="text-xs text-slate-500">{report.expenses?.length || 0} items</p>
                        </TableCell>
                        <TableCell>{getStatusBadge(report.status)}</TableCell>
                        <TableCell>
                          {report.status === 'Paid' ? (
                            <div className="flex items-center gap-2 text-sm">
                              {getPaymentIcon(report.payment_method)}
                              <div>
                                <p className="font-medium">{report.payment_method}</p>
                                {report.payment_reference && (
                                  <p className="text-xs text-slate-500">#{report.payment_reference}</p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setPreviewReport(report); setShowPreview(true); }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            {report.status === 'Submitted' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-500 text-white"
                                  onClick={() => handleApproveExpense(report.id)}
                                  disabled={approvingId === report.id}
                                >
                                  {approvingId === report.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleRejectExpense(report.id)}
                                  disabled={!!approvingId}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredExpenses.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No expense reports found
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timesheets Tab */}
        <TabsContent value="timesheets" className="space-y-4 mt-6">
          {/* Timesheet Filters and Actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPills
                    value={timesheetFilter}
                    onChange={setTimesheetFilter}
                    counts={{
                      pending:  timesheets.filter(t => t.status === 'Submitted').length,
                      approved: timesheets.filter(t => t.status === 'Approved').length,
                      paid:     timesheets.filter(t => t.status === 'Paid').length,
                      all:      timesheets.length,
                    }}
                    testIdPrefix="timesheet-pill"
                  />
                  <Badge variant="outline" className="text-slate-600">
                    {filteredTimesheets.length} timesheet(s)
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedTimesheets.length > 0 && timesheetFilter === 'approved' && (
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                      onClick={handleMarkTimesheetsPaid}
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Mark Paid ({selectedTimesheets.length})
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleExportTimesheetsCSV}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timesheets Table */}
          <Card>
            <CardContent className="p-0">
              {filteredTimesheets.length === 0 ? (
                <div className="p-8 text-center">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No {timesheetFilter === 'all' ? '' : timesheetFilter} timesheets found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        {timesheetFilter === 'approved' && (
                          <TableHead className="w-12">
                            <Checkbox 
                              checked={selectedTimesheets.length === filteredTimesheets.filter(t => t.status === 'Approved').length && selectedTimesheets.length > 0}
                              onCheckedChange={handleSelectAllTimesheets}
                            />
                          </TableHead>
                        )}
                        <TableHead>Employee</TableHead>
                        <TableHead>Week</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTimesheets.map((ts) => (
                        <TableRow
                          key={ts.id}
                          className="hover:bg-amber-50/40 cursor-pointer"
                          onClick={() => viewTimesheetDetails(ts)}
                          data-testid={`office-timesheet-row-${ts.id}`}
                        >
                          {timesheetFilter === 'approved' && (
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox 
                                checked={selectedTimesheets.includes(ts.id)}
                                onCheckedChange={(checked) => handleSelectTimesheet(ts.id, checked)}
                                disabled={ts.status !== 'Approved'}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-800">{ts.user_name}</p>
                              <p className="text-xs text-slate-500">{ts.timesheet_number || ts.id?.slice(0, 8)}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">Week {ts.week_number}, {ts.year}</p>
                              <p className="text-xs text-slate-500">{ts.week_start} to {ts.week_end}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <p className="font-semibold text-amber-600">{(ts.total_hours || 0).toFixed(2)}h</p>
                          </TableCell>
                          <TableCell>{getStatusBadge(ts.status)}</TableCell>
                          <TableCell>
                            {ts.submitted_at && (
                              <p className="text-sm text-slate-500">
                                {format(parseISO(ts.submitted_at), 'MMM d, h:mm a')}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => viewTimesheetDetails(ts)}
                                data-testid={`office-view-timesheet-${ts.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              {ts.status === 'Submitted' && (
                                <>
                                  <Button 
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-500 text-white"
                                    onClick={() => handleApproveTimesheet(ts.id)}
                                    disabled={approvingId === ts.id}
                                  >
                                    {approvingId === ts.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <CheckCircle2 className="w-4 h-4" />
                                    )}
                                  </Button>
                                  <Button 
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleRejectTimesheet(ts.id)}
                                    disabled={!!approvingId}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4 mt-6">
          {/* Summary Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Expense Stats */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <Receipt className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700">Total Expense Reports</p>
                    <p className="text-2xl font-bold text-blue-900">{expenseReports.length}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-200 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-blue-600">Pending</p>
                    <p className="font-semibold text-blue-800">{expenseReports.filter(r => r.status === 'Submitted').length}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Approved</p>
                    <p className="font-semibold text-blue-800">{expenseReports.filter(r => r.status === 'Approved').length}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Paid</p>
                    <p className="font-semibold text-blue-800">{expenseReports.filter(r => r.status === 'Paid').length}</p>
                  </div>
                  <div>
                    <p className="text-blue-600">Draft</p>
                    <p className="font-semibold text-blue-800">{expenseReports.filter(r => r.status === 'Draft').length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expense Amount Stats */}
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500 rounded-lg">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-700">Total Expense Amount</p>
                    <p className="text-2xl font-bold text-emerald-900">
                      {formatCurrency(expenseReports.reduce((sum, r) => sum + (r.total_amount || 0), 0))}
                    </p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-emerald-200 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-emerald-600">Pending</p>
                    <p className="font-semibold text-emerald-800">
                      {formatCurrency(expenseReports.filter(r => r.status === 'Submitted').reduce((sum, r) => sum + (r.total_amount || 0), 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-emerald-600">Approved</p>
                    <p className="font-semibold text-emerald-800">
                      {formatCurrency(expenseReports.filter(r => r.status === 'Approved').reduce((sum, r) => sum + (r.total_amount || 0), 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-emerald-600">Paid</p>
                    <p className="font-semibold text-emerald-800">
                      {formatCurrency(expenseReports.filter(r => r.status === 'Paid').reduce((sum, r) => sum + (r.total_amount || 0), 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-emerald-600">Avg per Report</p>
                    <p className="font-semibold text-emerald-800">
                      {formatCurrency(expenseReports.length > 0 ? expenseReports.reduce((sum, r) => sum + (r.total_amount || 0), 0) / expenseReports.length : 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timesheet Stats */}
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500 rounded-lg">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-700">Total Timesheets</p>
                    <p className="text-2xl font-bold text-amber-900">{timesheets.length}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-amber-200 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-amber-600">Pending</p>
                    <p className="font-semibold text-amber-800">{timesheets.filter(t => t.status === 'Submitted').length}</p>
                  </div>
                  <div>
                    <p className="text-amber-600">Approved</p>
                    <p className="font-semibold text-amber-800">{timesheets.filter(t => t.status === 'Approved').length}</p>
                  </div>
                  <div>
                    <p className="text-amber-600">Paid</p>
                    <p className="font-semibold text-amber-800">{timesheets.filter(t => t.status === 'Paid').length}</p>
                  </div>
                  <div>
                    <p className="text-amber-600">Draft</p>
                    <p className="font-semibold text-amber-800">{timesheets.filter(t => t.status === 'Draft').length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timesheet Hours Stats */}
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500 rounded-lg">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-700">Total Hours Logged</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {timesheets.reduce((sum, t) => sum + (t.total_hours || 0), 0).toFixed(2)}h
                    </p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-purple-200 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-purple-600">Pending</p>
                    <p className="font-semibold text-purple-800">
                      {timesheets.filter(t => t.status === 'Submitted').reduce((sum, t) => sum + (t.total_hours || 0), 0).toFixed(2)}h
                    </p>
                  </div>
                  <div>
                    <p className="text-purple-600">Approved</p>
                    <p className="font-semibold text-purple-800">
                      {timesheets.filter(t => t.status === 'Approved').reduce((sum, t) => sum + (t.total_hours || 0), 0).toFixed(2)}h
                    </p>
                  </div>
                  <div>
                    <p className="text-purple-600">Paid</p>
                    <p className="font-semibold text-purple-800">
                      {timesheets.filter(t => t.status === 'Paid').reduce((sum, t) => sum + (t.total_hours || 0), 0).toFixed(2)}h
                    </p>
                  </div>
                  <div>
                    <p className="text-purple-600">Avg per Sheet</p>
                    <p className="font-semibold text-purple-800">
                      {timesheets.length > 0 ? (timesheets.reduce((sum, t) => sum + (t.total_hours || 0), 0) / timesheets.length).toFixed(2) : 0}h
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Export Tools */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Barlow_Condensed']">Export Reports</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Report Type</Label>
                  <Select value={exportReportType} onValueChange={setExportReportType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="timesheets">Timesheets</SelectItem>
                      <SelectItem value="expenses">Expense Reports</SelectItem>
                      <SelectItem value="payroll">Payroll Summary (Approved Only)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <Select defaultValue="all">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {/* TODO: Populate with team members */}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input 
                    type="date" 
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input 
                    type="date" 
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-4">
                <Button 
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                  onClick={handleGenerateReportPDF}
                  disabled={generatingPDF}
                >
                  {generatingPDF ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  Generate PDF
                </Button>
                <Button variant="outline" onClick={handleExportCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  Export Expenses CSV
                </Button>
                <Button variant="outline" onClick={handleExportTimesheetsCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  Export Timesheets CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Paid lists previously lived here. They are now first-class
              tabs via the 'Paid' pill on the Timesheets and Expenses tabs,
              with the same print and email actions exposed inline. */}
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Mark as Paid
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Mark {selectedExpenses.length} expense report(s) as paid?
            </p>
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <Select value={paymentDetails.method} onValueChange={(v) => setPaymentDetails(prev => ({ ...prev, method: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Check">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Check
                    </div>
                  </SelectItem>
                  <SelectItem value="Direct Deposit">
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4" />
                      Direct Deposit
                    </div>
                  </SelectItem>
                  <SelectItem value="Cash">
                    <div className="flex items-center gap-2">
                      <Banknote className="w-4 h-4" />
                      Cash
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference # (Check number, Transaction ID)</Label>
              <Input 
                value={paymentDetails.reference}
                onChange={(e) => setPaymentDetails(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={processPayment} 
              disabled={processingPayment}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {processingPayment ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog with Receipt Viewing and Edit Capability */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {previewReport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-amber-500" />
                  {previewReport.report_number || `Week ${previewReport.week_number}, ${previewReport.year}`}
                  {getStatusBadge(previewReport.status)}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* User Info */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-semibold">{previewReport.user_name}</p>
                    <p className="text-sm text-slate-500">{previewReport.week_start} to {previewReport.week_end}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(previewReport.total_amount)}</p>
                    <p className="text-xs text-slate-500">{previewReport.expenses?.length || 0} items</p>
                  </div>
                </div>
                
                {/* Rejection Note for Returned Reports */}
                {previewReport.rejection_reason && (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg">
                    <p className="text-sm font-semibold text-amber-800">Returned for Corrections:</p>
                    <p className="text-sm text-amber-700">{previewReport.rejection_reason}</p>
                    {previewReport.rejected_by && (
                      <p className="text-xs text-amber-600 mt-1">By {previewReport.rejected_by}</p>
                    )}
                  </div>
                )}
                
                {/* Expenses List with Receipt Viewing */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-slate-700">Expenses ({previewReport.expenses?.length || 0})</h4>
                    {(isSuperUser || previewReport.status === 'Submitted') && (
                      <span className="text-xs text-slate-500">Click expense to edit</span>
                    )}
                  </div>
                  {previewReport.expenses?.map((expense, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 bg-white border rounded-lg hover:border-amber-300 transition-colors"
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-slate-900">{expense.date}</p>
                            <Badge variant="outline" className="text-xs">{expense.category}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1 truncate">{expense.description}</p>
                          {expense.job_number && (
                            <p className="text-xs text-amber-600 mt-1">Job: {expense.job_number}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="font-bold text-emerald-600">{formatCurrency(expense.amount)}</span>
                          <div className="flex items-center gap-1">
                            {expense.receipt_url ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingReceipt(expense.receipt_url);
                                  setShowReceiptDialog(true);
                                }}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View Receipt
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400 italic">No receipt</span>
                            )}
                            {/* Edit/Delete buttons for super_user or submitted status */}
                            {(isSuperUser || previewReport.status === 'Submitted') && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 px-2 text-slate-500 hover:text-amber-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingExpense({ ...expense, reportId: previewReport.id, index: idx });
                                    setShowEditExpenseDialog(true);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                {isSuperUser && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-slate-400 hover:text-red-500"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (window.confirm(`Delete this expense (${expense.description} - ${formatCurrency(expense.amount)})?`)) {
                                        try {
                                          await axios.delete(
                                            `${API}/api/expense-reports/${previewReport.id}/expense/${idx}`,
                                            { headers: { Authorization: `Bearer ${token}` } }
                                          );
                                          toast.success('Expense deleted');
                                          // Refresh the preview report
                                          const updatedReport = await axios.get(
                                            `${API}/api/expense-reports/${previewReport.id}`,
                                            { headers: { Authorization: `Bearer ${token}` } }
                                          );
                                          setPreviewReport(updatedReport.data);
                                          fetchData();
                                        } catch (error) {
                                          toast.error(error.response?.data?.detail || 'Failed to delete expense');
                                        }
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <span className="font-semibold text-slate-700">Total Amount</span>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(previewReport.total_amount)}</span>
                </div>

                {/* Payment Info for Paid Reports */}
                {previewReport.status === 'Paid' && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-semibold text-emerald-700 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Payment Information
                    </h4>
                    <div className="mt-2 text-sm text-emerald-700">
                      <p>Method: {previewReport.payment_method}</p>
                      {previewReport.payment_reference && <p>Reference: #{previewReport.payment_reference}</p>}
                      <p>Paid: {previewReport.paid_at ? format(parseISO(previewReport.paid_at), 'MMM d, yyyy h:mm a') : '-'}</p>
                      <p>By: {previewReport.paid_by}</p>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Close
                </Button>
                {/* Single PDF button - opens NEW style print view */}
                <Button 
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => {
                    const report = previewReport;
                    const printWindow = window.open('', '_blank');
                    if (!printWindow) {
                      toast.error('Please allow popups to open print view');
                      return;
                    }
                    
                    const html = `
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Expense Report - ${report.user_name} - Week ${report.week_number}, ${report.year}</title>
                        <style>
                          * { margin: 0; padding: 0; box-sizing: border-box; }
                          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 850px; margin: 0 auto; background: #fff; color: #1e293b; }
                          
                          /* Header - 3 column layout matching new style */
                          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #e2e8f0; }
                          .logo-section { flex-shrink: 0; }
                          .logo-section img { height: 80px; width: auto; object-fit: contain; }
                          .company-info { flex: 1; text-align: center; padding: 0 20px; font-size: 12px; color: #64748b; line-height: 1.5; }
                          .company-info .address { font-weight: 500; color: #334155; }
                          .company-info .address-secondary { color: #64748b; margin-top: 2px; }
                          .company-info .contact { margin-top: 6px; display: flex; align-items: center; justify-content: center; gap: 16px; font-size: 11px; }
                          .company-info .contact span { display: flex; align-items: center; gap: 4px; }
                          .company-info .contact svg { width: 12px; height: 12px; color: #64748b; }
                          .doc-id { text-align: right; flex-shrink: 0; }
                          .doc-id .doc-label { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; }
                          .doc-id .doc-number { font-size: 16px; font-weight: 700; color: #1e293b; margin-top: 2px; }
                          
                          /* Info Section */
                          .info-section { background: #f8fafc; border-radius: 8px; padding: 20px; margin-bottom: 25px; }
                          .info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
                          .info-item { text-align: center; }
                          .info-item label { font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px; }
                          .info-item span { font-size: 14px; color: #1e293b; font-weight: 500; }
                          .info-item .large { font-size: 24px; font-weight: 700; color: #16a34a; }
                          
                          /* Table */
                          table { width: 100%; border-collapse: collapse; margin-bottom: 25px; font-size: 12px; }
                          th { background: #f1f5f9; color: #475569; padding: 12px 10px; text-align: left; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; }
                          th.text-right { text-align: right; }
                          td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
                          td.text-right { text-align: right; }
                          tr:hover { background: #f8fafc; }
                          .total-row { background: #f8fafc !important; font-weight: 600; }
                          .total-row td { border-bottom: none; border-top: 2px solid #e2e8f0; }
                          .amount { font-weight: 600; color: #16a34a; }
                          .category { display: inline-block; padding: 2px 8px; background: #e0f2fe; color: #0369a1; border-radius: 4px; font-size: 11px; }
                          
                          /* Notes */
                          .notes { padding: 15px; background: #eff6ff; border-radius: 8px; margin-bottom: 20px; }
                          .notes-label { font-size: 11px; font-weight: 600; color: #1e40af; margin-bottom: 4px; }
                          .notes-text { font-size: 13px; color: #1e3a8a; }
                          
                          /* Signature Section */
                          .signature-section { margin-top: 50px; display: flex; justify-content: space-between; gap: 40px; }
                          .signature-box { flex: 1; }
                          .signature-box label { font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 30px; }
                          .signature-line { border-top: 1px solid #1e293b; padding-top: 8px; font-size: 12px; color: #64748b; }
                          
                          /* Print Button */
                          .print-btn { 
                            position: fixed; top: 20px; right: 20px; 
                            background: #ea580c; color: white; border: none; 
                            padding: 12px 24px; font-size: 14px; font-weight: 600;
                            cursor: pointer; border-radius: 6px;
                            display: flex; align-items: center; gap: 8px;
                          }
                          .print-btn:hover { background: #c2410c; }
                          
                          @media print { 
                            .print-btn { display: none; } 
                            body { padding: 20px; }
                            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                          }
                        </style>
                      </head>
                      <body>
                        <button class="print-btn" onclick="window.print()">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                          Print / Save PDF
                        </button>
                        
                        <!-- Header - New 3-column layout -->
                        <div class="header">
                          <div class="logo-section">
                            <img src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69b5afabd540121a86879b5b/e29b4c6b2_CLLLogoTransparent.png" alt="Center Line Locating" />
                          </div>
                          <div class="company-info">
                            <div class="address">12030 Goodrich Dr, Charlotte, NC 28273</div>
                            <div class="address-secondary">PO Box 38251, Charlotte, NC 28278</div>
                            <div class="contact">
                              <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>(704) 315-5820</span>
                              <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>requests@cllocating.com</span>
                            </div>
                          </div>
                          <div class="doc-id">
                            <div class="doc-label">Expense Report</div>
                            <div class="doc-number">#${report.report_number || 'Draft'}</div>
                          </div>
                        </div>
                        
                        <!-- Info Section -->
                        <div class="info-section">
                          <div class="info-grid">
                            <div class="info-item">
                              <span class="large">$${(report.total_amount || 0).toFixed(2)}</span>
                              <label>Total Amount</label>
                            </div>
                            <div class="info-item">
                              <span>${report.user_name}</span>
                              <label>Employee</label>
                            </div>
                            <div class="info-item">
                              <span>Week ${report.week_number}, ${report.year}</span>
                              <label>Period</label>
                            </div>
                            <div class="info-item">
                              <span>${report.status}</span>
                              <label>Status</label>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Expenses Table -->
                        <table>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Category</th>
                              <th>Description</th>
                              <th class="text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${(report.expenses || []).map(expense => `
                              <tr>
                                <td>${expense.date ? (() => { const p = expense.date.split('-'); return new Date(p[0], p[1]-1, p[2]).toLocaleDateString(); })() : '-'}</td>
                                <td><span class="category">${expense.category || '-'}</span></td>
                                <td>${expense.description || '-'}</td>
                                <td class="text-right amount">$${(expense.amount || 0).toFixed(2)}</td>
                              </tr>
                            `).join('')}
                            <tr class="total-row">
                              <td colspan="3" style="text-align: right; font-weight: 600;">Total:</td>
                              <td class="text-right amount">$${(report.total_amount || 0).toFixed(2)}</td>
                            </tr>
                          </tbody>
                        </table>
                        
                        ${report.notes ? `
                          <div class="notes">
                            <div class="notes-label">Notes</div>
                            <div class="notes-text">${report.notes}</div>
                          </div>
                        ` : ''}
                        
                        <!-- Signature Section -->
                        <div class="signature-section">
                          <div class="signature-box">
                            <label>Employee Signature</label>
                            <div class="signature-line">${report.user_name} • ${report.submitted_at ? new Date(report.submitted_at).toLocaleDateString() : ''}</div>
                          </div>
                          <div class="signature-box">
                            <label>Supervisor Signature</label>
                            <div class="signature-line">${report.approved_by || ''} • ${report.approved_at ? new Date(report.approved_at).toLocaleDateString() : ''}</div>
                          </div>
                        </div>
                      </body>
                      </html>
                    `;
                    
                    printWindow.document.write(html);
                    printWindow.document.close();
                    toast.success('Print view opened');
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                {previewReport.status === 'Submitted' && (
                  <>
                    <Button 
                      variant="outline" 
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => { handleRejectExpense(previewReport.id); setShowPreview(false); }}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button 
                      className="bg-green-500 hover:bg-green-600"
                      onClick={() => { handleApproveExpense(previewReport.id); setShowPreview(false); }}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                  </>
                )}
                {/* Delete Report button for super_user */}
                {isSuperUser && (
                  <Button 
                    variant="outline" 
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={async () => {
                      if (window.confirm(`Delete this entire expense report (${previewReport.report_number || 'Week ' + previewReport.week_number})? This cannot be undone.`)) {
                        try {
                          await axios.delete(
                            `${API}/api/expense-reports/${previewReport.id}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setShowPreview(false);
                          setPreviewReport(null);
                          toast.success('Expense report deleted');
                          setTimeout(() => fetchData(), 100);
                        } catch (error) {
                          console.error('Delete error:', error);
                          toast.error(error.response?.data?.detail || 'Failed to delete report');
                        }
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Report
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Viewer Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Receipt</DialogTitle>
          </DialogHeader>
          {viewingReceipt && (
            <div className="flex items-center justify-center p-4 bg-slate-100 min-h-[400px]">
              {viewingReceipt.toLowerCase().includes('.pdf') ? (
                <div className="text-center">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">PDF Receipt</p>
                  <Button onClick={() => window.open(viewingReceipt, '_blank')}>
                    <Eye className="w-4 h-4 mr-2" />
                    Open in New Tab
                  </Button>
                </div>
              ) : (
                <img 
                  src={viewingReceipt} 
                  alt="Receipt" 
                  className="max-w-full max-h-[70vh] object-contain rounded shadow-lg"
                />
              )}
            </div>
          )}
          <DialogFooter className="p-4 border-t">
            <Button variant="outline" onClick={() => setShowReceiptDialog(false)}>
              Close
            </Button>
            <Button onClick={() => window.open(viewingReceipt, '_blank')}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Dialog - For approvers to make small corrections */}
      <Dialog open={showEditExpenseDialog} onOpenChange={setShowEditExpenseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-500" />
              Edit Expense
            </DialogTitle>
          </DialogHeader>
          {editingExpense && (
            <div className="space-y-4 py-4">
              <p className="text-xs text-slate-500">Make minor corrections before approving</p>
              
              <div className="space-y-2">
                <Label>Date</Label>
                <Input 
                  type="date"
                  value={editingExpense.date}
                  onChange={(e) => setEditingExpense(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={editingExpense.category}
                  onValueChange={(v) => setEditingExpense(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fuel/Mileage">Fuel/Mileage</SelectItem>
                    <SelectItem value="Materials/Supplies">Materials/Supplies</SelectItem>
                    <SelectItem value="Equipment">Equipment</SelectItem>
                    <SelectItem value="Meals">Meals</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Input 
                  value={editingExpense.description}
                  onChange={(e) => setEditingExpense(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={editingExpense.amount ?? ''}
                  onChange={(e) => setEditingExpense(prev => ({ ...prev, amount: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditExpenseDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              onClick={handleSaveExpenseEdit}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timesheet Preview Dialog */}
      <Dialog open={showTimesheetPreview} onOpenChange={setShowTimesheetPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {previewTimesheet && (
            <>
              <DialogHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <div>
                    <DialogTitle className="text-xl">{previewTimesheet.user_name}'s Timesheet</DialogTitle>
                    <p className="text-sm text-slate-500">
                      Week {previewTimesheet.week_number}, {previewTimesheet.year} • {previewTimesheet.week_start} to {previewTimesheet.week_end}
                    </p>
                  </div>
                  {getStatusBadge(previewTimesheet.status)}
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{(previewTimesheet.total_hours || 0).toFixed(2)}h</p>
                    <p className="text-xs text-slate-500">Total Hours</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-700">{previewTimesheet.entries?.length || 0}</p>
                    <p className="text-xs text-slate-500">Entries</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-slate-600">
                      {previewTimesheet.submitted_at ? format(parseISO(previewTimesheet.submitted_at), 'MMM d, h:mm a') : '-'}
                    </p>
                    <p className="text-xs text-slate-500">Submitted</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-slate-600">{previewTimesheet.approved_by || '-'}</p>
                    <p className="text-xs text-slate-500">Approved By</p>
                  </div>
                </div>

                {/* Time Entries */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Date</TableHead>
                        <TableHead>Job</TableHead>
                        <TableHead className="text-right">Travel To</TableHead>
                        <TableHead className="text-right">On Job</TableHead>
                        <TableHead className="text-right">Travel From</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(previewTimesheet.entries || []).map((entry, idx) => (
                        <TableRow key={entry.id || idx}>
                          <TableCell className="font-medium">{entry.date}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-800">{entry.job_number || 'OTHER'}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[150px]">{entry.job_title || entry.custom_description}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{(entry.travel_to_job || 0).toFixed(2)}h</TableCell>
                          <TableCell className="text-right">{(entry.time_on_job || 0).toFixed(2)}h</TableCell>
                          <TableCell className="text-right">{(entry.travel_from_job || 0).toFixed(2)}h</TableCell>
                          <TableCell className="text-right font-semibold text-amber-600">
                            {(entry.total_hours || 0).toFixed(2)}h
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Notes */}
                {previewTimesheet.notes && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-800">Notes:</p>
                    <p className="text-sm text-blue-700">{previewTimesheet.notes}</p>
                  </div>
                )}

                {/* Rejection reason */}
                {previewTimesheet.rejection_reason && (
                  <div className="p-3 bg-red-50 rounded-lg">
                    <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                    <p className="text-sm text-red-700">{previewTimesheet.rejection_reason}</p>
                  </div>
                )}

                {/* Payment info */}
                {previewTimesheet.status === 'Paid' && (
                  <div className="p-3 bg-emerald-50 rounded-lg flex items-center gap-3">
                    {getPaymentIcon(previewTimesheet.payment_method)}
                    <div>
                      <p className="text-sm font-medium text-emerald-800">
                        Paid via {previewTimesheet.payment_method}
                        {previewTimesheet.payment_reference && ` (${previewTimesheet.payment_reference})`}
                      </p>
                      <p className="text-xs text-emerald-600">
                        {previewTimesheet.paid_at && format(parseISO(previewTimesheet.paid_at), 'MMM d, yyyy h:mm a')}
                        {previewTimesheet.paid_by && ` by ${previewTimesheet.paid_by}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="mt-4 flex-wrap gap-2">
                <Button variant="outline" onClick={() => setShowTimesheetPreview(false)}>
                  Close
                </Button>
                {/* Single PDF button - opens NEW style print view */}
                <Button 
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => {
                    const ts = previewTimesheet;
                    const printWindow = window.open('', '_blank');
                    if (!printWindow) {
                      toast.error('Please allow popups to open print view');
                      return;
                    }
                    
                    const html = `
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Timesheet - ${ts.user_name} - Week ${ts.week_number} - ${ts.year}</title>
                        <style>
                          * { margin: 0; padding: 0; box-sizing: border-box; }
                          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 850px; margin: 0 auto; background: #fff; color: #1e293b; }
                          
                          /* Header - 3 column layout matching new style */
                          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #e2e8f0; }
                          .logo-section { flex-shrink: 0; }
                          .logo-section img { height: 80px; width: auto; object-fit: contain; }
                          .company-info { flex: 1; text-align: center; padding: 0 20px; font-size: 12px; color: #64748b; line-height: 1.5; }
                          .company-info .address { font-weight: 500; color: #334155; }
                          .company-info .address-secondary { color: #64748b; margin-top: 2px; }
                          .company-info .contact { margin-top: 6px; display: flex; align-items: center; justify-content: center; gap: 16px; font-size: 11px; }
                          .company-info .contact span { display: flex; align-items: center; gap: 4px; }
                          .company-info .contact svg { width: 12px; height: 12px; color: #64748b; }
                          .doc-id { text-align: right; flex-shrink: 0; }
                          .doc-id .doc-label { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; }
                          .doc-id .doc-number { font-size: 16px; font-weight: 700; color: #1e293b; margin-top: 2px; }
                          
                          /* Info Section */
                          .info-section { background: #f8fafc; border-radius: 8px; padding: 20px; margin-bottom: 25px; }
                          .info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
                          .info-item { text-align: center; }
                          .info-item label { font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 4px; }
                          .info-item span { font-size: 14px; color: #1e293b; font-weight: 500; }
                          .info-item .large { font-size: 24px; font-weight: 700; color: #f59e0b; }
                          
                          /* Table */
                          table { width: 100%; border-collapse: collapse; margin-bottom: 25px; font-size: 12px; }
                          th { background: #f1f5f9; color: #475569; padding: 12px 10px; text-align: left; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; }
                          th.text-right { text-align: right; }
                          td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
                          td.text-right { text-align: right; }
                          tr:hover { background: #f8fafc; }
                          .total-row { background: #f8fafc !important; font-weight: 600; }
                          .total-row td { border-bottom: none; border-top: 2px solid #e2e8f0; }
                          .job-number { font-weight: 600; color: #1e293b; }
                          .job-title { font-size: 11px; color: #64748b; }
                          .hours-total { font-weight: 600; color: #f59e0b; }
                          
                          /* Notes */
                          .notes { padding: 15px; background: #eff6ff; border-radius: 8px; margin-bottom: 20px; }
                          .notes-label { font-size: 11px; font-weight: 600; color: #1e40af; margin-bottom: 4px; }
                          .notes-text { font-size: 13px; color: #1e3a8a; }
                          
                          /* Signature Section */
                          .signature-section { margin-top: 50px; display: flex; justify-content: space-between; gap: 40px; }
                          .signature-box { flex: 1; }
                          .signature-box label { font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 30px; }
                          .signature-line { border-top: 1px solid #1e293b; padding-top: 8px; font-size: 12px; color: #64748b; }
                          
                          /* Print Button */
                          .print-btn { 
                            position: fixed; top: 20px; right: 20px; 
                            background: #ea580c; color: white; border: none; 
                            padding: 12px 24px; font-size: 14px; font-weight: 600;
                            cursor: pointer; border-radius: 6px;
                            display: flex; align-items: center; gap: 8px;
                          }
                          .print-btn:hover { background: #c2410c; }
                          
                          @media print { 
                            .print-btn { display: none; } 
                            body { padding: 20px; }
                            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                          }
                        </style>
                      </head>
                      <body>
                        <button class="print-btn" onclick="window.print()">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                          Print / Save PDF
                        </button>
                        
                        <!-- Header - New 3-column layout -->
                        <div class="header">
                          <div class="logo-section">
                            <img src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/69b5afabd540121a86879b5b/e29b4c6b2_CLLLogoTransparent.png" alt="Center Line Locating" />
                          </div>
                          <div class="company-info">
                            <div class="address">12030 Goodrich Dr, Charlotte, NC 28273</div>
                            <div class="address-secondary">PO Box 38251, Charlotte, NC 28278</div>
                            <div class="contact">
                              <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>(704) 315-5820</span>
                              <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>requests@cllocating.com</span>
                            </div>
                          </div>
                          <div class="doc-id">
                            <div class="doc-label">Timesheet</div>
                            <div class="doc-number">#${ts.timesheet_number || `TS-${ts.week_number}-${ts.year}`}</div>
                          </div>
                        </div>
                        
                        <!-- Info Section -->
                        <div class="info-section">
                          <div class="info-grid">
                            <div class="info-item">
                              <span class="large">${(ts.total_hours || 0).toFixed(2)}h</span>
                              <label>Total Hours</label>
                            </div>
                            <div class="info-item">
                              <span>${ts.user_name}</span>
                              <label>Employee</label>
                            </div>
                            <div class="info-item">
                              <span>Week ${ts.week_number}, ${ts.year}</span>
                              <label>Period</label>
                            </div>
                            <div class="info-item">
                              <span>${ts.week_start} - ${ts.week_end}</span>
                              <label>Dates</label>
                            </div>
                          </div>
                        </div>
                        
                        <!-- Time Entries Table -->
                        <table>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Job</th>
                              <th class="text-right">Travel To</th>
                              <th class="text-right">On Job</th>
                              <th class="text-right">Travel From</th>
                              <th class="text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${(ts.entries || []).map(entry => `
                              <tr>
                                <td>${entry.date}</td>
                                <td>
                                  <div class="job-number">${entry.job_number || 'OTHER'}</div>
                                  <div class="job-title">${entry.job_title || entry.custom_description || ''}</div>
                                </td>
                                <td class="text-right">${(entry.travel_to_job || 0).toFixed(2)}h</td>
                                <td class="text-right">${(entry.time_on_job || 0).toFixed(2)}h</td>
                                <td class="text-right">${(entry.travel_from_job || 0).toFixed(2)}h</td>
                                <td class="text-right hours-total">${(entry.total_hours || 0).toFixed(2)}h</td>
                              </tr>
                            `).join('')}
                            <tr class="total-row">
                              <td colspan="5" style="text-align: right; font-weight: 600;">Total Hours:</td>
                              <td class="text-right hours-total">${(ts.total_hours || 0).toFixed(2)}h</td>
                            </tr>
                          </tbody>
                        </table>
                        
                        ${ts.notes ? `
                          <div class="notes">
                            <div class="notes-label">Notes</div>
                            <div class="notes-text">${ts.notes}</div>
                          </div>
                        ` : ''}
                        
                        <!-- Signature Section -->
                        <div class="signature-section">
                          <div class="signature-box">
                            <label>Employee Signature</label>
                            <div class="signature-line">${ts.user_name} • ${ts.submitted_at ? new Date(ts.submitted_at).toLocaleDateString() : ''}</div>
                          </div>
                          <div class="signature-box">
                            <label>Supervisor Signature</label>
                            <div class="signature-line">${ts.approved_by || ''} • ${ts.approved_at ? new Date(ts.approved_at).toLocaleDateString() : ''}</div>
                          </div>
                        </div>
                      </body>
                      </html>
                    `;
                    
                    printWindow.document.write(html);
                    printWindow.document.close();
                    toast.success('Print view opened');
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                {previewTimesheet.status === 'Submitted' && (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => handleRejectTimesheet(previewTimesheet.id)}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-500 text-white"
                      onClick={() => handleApproveTimesheet(previewTimesheet.id)}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Timesheet Payment Dialog */}
      <Dialog open={showTimesheetPaymentDialog} onOpenChange={setShowTimesheetPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Timesheets as Paid</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              You are marking <strong>{selectedTimesheets.length}</strong> timesheet(s) as paid.
            </p>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={timesheetPaymentDetails.method} onValueChange={(v) => setTimesheetPaymentDetails(prev => ({ ...prev, method: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="Direct Deposit">Direct Deposit</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference / Check Number</Label>
              <Input 
                placeholder="e.g., Check #1234"
                value={timesheetPaymentDetails.reference}
                onChange={(e) => setTimesheetPaymentDetails(prev => ({ ...prev, reference: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimesheetPaymentDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              onClick={processTimesheetPayment}
              disabled={processingPayment}
            >
              {processingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfficeDashboard;
